import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Send, RotateCcw, Loader2, Home, Thermometer, Car, Scissors, Building2, Dumbbell, SmilePlus, ArrowLeft, Sparkles, GraduationCap } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const businesses = [
  { id: "coaches", label: "Coaches", icon: GraduationCap, name: "Coach Sarah | Strategy", suggestion: "I saw your ad about the free strategy call", color: "from-blue-500 to-blue-600" },
  { id: "hvac", label: "HVAC", icon: Thermometer, name: "CoolBreeze HVAC", suggestion: "My AC stopped blowing cold air", color: "from-orange-500 to-red-500" },
  { id: "kitchen", label: "Home Services", icon: Home, name: "Mike's Home Services", suggestion: "How much for a full kitchen reno?", color: "from-emerald-500 to-emerald-600" },
  { id: "hairstylist", label: "Hair Styling", icon: Scissors, name: "Luxe Hair Studio", suggestion: "Do you do balayage? How much?", color: "from-pink-500 to-rose-500" },
  { id: "cardealership", label: "Car Dealership", icon: Car, name: "Prestige Motors", suggestion: "Got any SUVs under 35k?", color: "from-violet-500 to-purple-600" },
  { id: "dental", label: "Dental", icon: SmilePlus, name: "Bright Smile Dental", suggestion: "Need to book a cleaning, new patient", color: "from-cyan-500 to-blue-500" },
  { id: "fitness", label: "Fitness", icon: Dumbbell, name: "Iron Peak Fitness", suggestion: "What memberships do you have?", color: "from-amber-500 to-orange-500" },
  { id: "cleaning", label: "Cleaning", icon: Sparkles, name: "Spotless Cleaning Co", suggestion: "How much for a deep clean 3-bed house?", color: "from-teal-500 to-cyan-500" },
];

function generateSessionId(bizId) {
  return `demo-${bizId}-${Math.random().toString(36).substring(2, 10)}`;
}

export default function LiveDemo() {
  const [activeBiz, setActiveBiz] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);

  const selectBusiness = (biz) => {
    setActiveBiz(biz);
    setMessages([]);
    setInput("");
    setSessionId(generateSessionId(biz.id));
  };

  const goBack = async () => {
    if (sessionId) {
      try { await axios.delete(`${API}/chat/${sessionId}`); } catch (e) { /* ignore */ }
    }
    setActiveBiz(null);
    setMessages([]);
    setInput("");
  };

  const sendMessage = async (overrideText) => {
    const text = (overrideText || input).trim();
    if (!text || loading || !activeBiz) return;

    const userMsg = { role: "user", content: text };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const res = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: text,
        business_type: activeBiz.id,
      });
      const aiMsg = { role: "assistant", content: res.data.response };
      setMessages((prev) => [...prev, aiMsg]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "hmm something went wrong, try again?" },
      ]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const resetChat = async () => {
    try { await axios.delete(`${API}/chat/${sessionId}`); } catch (e) { /* ignore */ }
    setMessages([]);
    setSessionId(generateSessionId(activeBiz.id));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <section id="demo" data-testid="demo-section" className="py-24 md:py-32 relative scroll-mt-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10 md:mb-14"
        >
          <span className="text-blue-400 text-sm font-semibold tracking-wide uppercase mb-4 block">Live Demo</span>
          <h2
            data-testid="demo-heading"
            className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight mb-4"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            Try it yourself
          </h2>
          <p className="text-zinc-400 text-base md:text-lg max-w-2xl mx-auto">
            Pick your industry and text our AI like you're a real customer
          </p>
        </motion.div>

        <AnimatePresence mode="wait">
          {!activeBiz ? (
            /* ---- BUSINESS SELECTION GRID ---- */
            <motion.div
              key="grid"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.3 }}
              className="max-w-3xl mx-auto"
              data-testid="business-grid"
            >
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {businesses.map((biz, i) => {
                  const Icon = biz.icon;
                  return (
                    <motion.button
                      key={biz.id}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: i * 0.04 }}
                      data-testid={`biz-select-${biz.id}`}
                      onClick={() => selectBusiness(biz)}
                      className="group flex flex-col items-center gap-3 bg-zinc-900/50 hover:bg-zinc-800/70 border border-white/5 hover:border-white/15 rounded-2xl p-5 md:p-6 transition-all duration-200 cursor-pointer"
                    >
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${biz.color} flex items-center justify-center shadow-lg transition-transform duration-200 group-hover:scale-110`}>
                        <Icon size={22} className="text-white" />
                      </div>
                      <span className="text-zinc-300 group-hover:text-white text-sm font-medium transition-colors text-center">
                        {biz.label}
                      </span>
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          ) : (
            /* ---- CHAT VIEW ---- */
            <motion.div
              key="chat"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.3 }}
              className="max-w-2xl mx-auto"
            >
              {/* Back button */}
              <button
                data-testid="back-to-businesses"
                onClick={goBack}
                className="flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm font-medium mb-3 transition-colors"
              >
                <ArrowLeft size={16} />
                Choose different business
              </button>

              <div
                data-testid="chat-container"
                className="bg-zinc-900/60 border border-white/5 rounded-2xl overflow-hidden backdrop-blur-xl shadow-2xl"
              >
                {/* Chat header */}
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/5 bg-zinc-900/80">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${activeBiz.color} flex items-center justify-center`}>
                      <span className="text-white text-sm font-bold">{activeBiz.name[0]}</span>
                    </div>
                    <div>
                      <p className="text-white text-sm font-semibold">{activeBiz.name}</p>
                      <p className="text-emerald-400 text-[11px]">Online — typically replies instantly</p>
                    </div>
                  </div>
                  <Button
                    data-testid="reset-chat-button"
                    variant="ghost"
                    size="sm"
                    onClick={resetChat}
                    className="text-zinc-400 hover:text-white h-8 w-8 p-0"
                  >
                    <RotateCcw size={16} />
                  </Button>
                </div>

                {/* Messages */}
                <ScrollArea className="h-[360px] md:h-[400px]">
                  <div className="px-5 py-4 space-y-3">
                    {messages.length === 0 && (
                      <div className="text-center py-10">
                        <p className="text-zinc-500 text-sm mb-4">Text {activeBiz.name} as a customer</p>
                        <button
                          data-testid="chat-suggestion"
                          onClick={() => sendMessage(activeBiz.suggestion)}
                          className="text-xs text-zinc-300 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-full px-4 py-2 transition-colors"
                        >
                          "{activeBiz.suggestion}"
                        </button>
                      </div>
                    )}

                    <AnimatePresence>
                      {messages.map((msg, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, y: 10, scale: 0.97 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          transition={{ duration: 0.25 }}
                          className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                        >
                          <div
                            className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                              msg.role === "user"
                                ? "blue-bubble text-white rounded-br-md"
                                : "grey-bubble text-zinc-100 rounded-bl-md"
                            }`}
                          >
                            {msg.content}
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>

                    {loading && (
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start">
                        <div className="grey-bubble px-4 py-3 rounded-2xl rounded-bl-md flex gap-1.5">
                          <span className="typing-dot w-1.5 h-1.5 bg-zinc-400 rounded-full inline-block" />
                          <span className="typing-dot w-1.5 h-1.5 bg-zinc-400 rounded-full inline-block" />
                          <span className="typing-dot w-1.5 h-1.5 bg-zinc-400 rounded-full inline-block" />
                        </div>
                      </motion.div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>

                {/* Input */}
                <div className="px-4 py-3 border-t border-white/5 bg-zinc-900/80 flex gap-2">
                  <Input
                    ref={inputRef}
                    data-testid="chat-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type a message..."
                    disabled={loading}
                    className="bg-zinc-800/50 border-none text-white rounded-xl px-4 py-2.5 focus-visible:ring-1 focus-visible:ring-blue-500 placeholder-zinc-500 text-sm flex-1"
                  />
                  <Button
                    data-testid="send-button"
                    onClick={() => sendMessage()}
                    disabled={loading || !input.trim()}
                    className="bg-blue-600 hover:bg-blue-500 text-white rounded-xl px-4 h-10 transition-all disabled:opacity-40"
                  >
                    {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                  </Button>
                </div>
              </div>

              <p className="text-zinc-600 text-xs text-center mt-4">
                Powered by AI. In production, responses are fully customized for your specific business.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
